/**
 * Category Cockpit — the small shared pieces: state pill, four dots, ten aspect dots, tier
 * pill, L1/L3/L5 mini-bars, horizontal bar, sparkline, empty state, "Sources" line, panels.
 * Colour contract: the pill states ARE the data (ready · attention · blocked · todo · off).
 */
import React from 'react';
import type { ActionState, Dot, Derived, SiteCell } from '@/services/categoryCockpitService';
import { GATES, day, fmt } from '../lib/format';

export function StatePill({ k, title, children, small }: { k: ActionState | string; title?: string; children: React.ReactNode; small?: boolean }) {
  return <span className={`pmk-state pmk-${k}${small ? ' pmk-sm' : ''}`} data-tip={title}>{children}</span>;
}

const DOT_NAMES = ['have', 'good', 'tested', 'versioned'];
const DOT_WORD: Record<Dot, string> = { f: 'yes', h: 'partly', '': 'no' };

/** have · good · tested · versioned */
export function Dots({ dots }: { dots: [Dot, Dot, Dot, Dot] }) {
  const title = dots.map((x, i) => `${DOT_NAMES[i]}: ${DOT_WORD[x]}`).join(' · ');
  return (
    <span className="pmk-rdots" data-tip={title} aria-label={title}>
      {dots.map((x, i) => <i key={DOT_NAMES[i]} className={x} />)}
    </span>
  );
}

/** the ten aspects, one dot each (fresh · stale · empty · never · error) */
export function AspectDots({ aspects }: { aspects: Derived['aspects'] }) {
  return (
    <span className="pmk-adots">
      {aspects.map((x) => <i key={x.a} className={x.state} data-tip={`${x.a}: ${x.state}${x.n ? ` · ${x.n} entries` : ''}`} />)}
    </span>
  );
}

export function Ladder({ l }: { l: [Dot, Dot, Dot, Dot] }) {
  return (
    <span className="pmk-ladder">
      {l.map((x, i) => <span key={DOT_NAMES[i]} className={x} data-tip={`${DOT_NAMES[i]}: ${x === 'f' ? 'yes' : x === 'h' ? 'partly' : 'no'}`}>{DOT_NAMES[i]}</span>)}
    </span>
  );
}

export function TierPill({ site }: { site: SiteCell | null | undefined }) {
  if (!site || !site.tier) return <span className="pmk-tier none">—</span>;
  return <span className={`pmk-tier t-${site.tier}`} data-tip={`overall ${Number(site.overall_score ?? 0).toFixed(2)} · scored ${day(site.scored_at)}`}>{site.tier}</span>;
}

export function MiniBars({ site }: { site: SiteCell }) {
  if (!site.tier) return null;
  const keys = ['l1', 'l3', 'l5'] as const;
  const title = `L1 ${Number(site.l1_score ?? 0).toFixed(2)} / L3 ${Number(site.l3_score ?? 0).toFixed(2)} / L5 ${Number(site.l5_score ?? 0).toFixed(2)} · gates .80 / .70 / .60`;
  return (
    <span className="pmk-minibars" data-tip={title}>
      {keys.map((k) => {
        const v = Number(site[`${k}_score`] ?? 0);
        return <b key={k} className={v < GATES[k] ? 'under' : ''}><span style={{ height: `${Math.round(100 * v)}%` }} /></b>;
      })}
    </span>
  );
}

export function Bar({ v, gate, cls }: { v: number; gate?: number; cls?: string }) {
  const w = Math.round(100 * Math.min(1, Math.max(0, v || 0)));
  return (
    <span className={`pmk-bar ${cls || ''}`} data-tip={`${w} %`}>
      <span style={{ width: `${w}%` }} />
      {gate != null && <em style={{ left: `${gate * 100}%` }} />}
    </span>
  );
}

export function Sparkline({ series, label }: { series: Array<{ day: string; overall_score: number }>; label: string }) {
  if (!series || series.length < 2) return <span className="pmc-mute pmc-small">no history yet</span>;
  const w = 300; const h = 46; const pad = 3;
  const vals = series.map((p) => Number(p.overall_score) || 0);
  const n = vals.length;
  const pts = vals.map((v, i) => [pad + (i * (w - 2 * pad)) / (n - 1), h - pad - v * (h - 2 * pad)]);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const gateY = h - pad - 0.6 * (h - 2 * pad);
  return (
    <div className="pmk-spark">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label={`${label}: readiness over ${n} days`}>
        <line x1="0" x2={w} y1={gateY} y2={gateY} stroke="currentColor" strokeOpacity=".2" strokeDasharray="3 3" />
        <path d={d} fill="none" stroke="var(--pmc-accent)" strokeWidth="1.5" />
        <circle cx={pts[n - 1][0]} cy={pts[n - 1][1]} r="2.5" fill="var(--pmc-accent)" />
      </svg>
      <div className="pmc-small pmc-mute">{day(series[0].day)} → {day(series[n - 1].day)} · {n} snapshots · overall {vals[0].toFixed(2)} → {vals[n - 1].toFixed(2)}</div>
    </div>
  );
}

export function EmptyState({ title, children, src, actions }: { title: string; children?: React.ReactNode; src?: string; actions?: React.ReactNode }) {
  return (
    <div className="pmk-empty">
      <b>{title}</b>
      {children && <div className="pmk-empty-body">{children}</div>}
      {src && <Src>{src}</Src>}
      {actions && <div className="pmk-actions">{actions}</div>}
    </div>
  );
}

/** a line shown only when the Sources toggle is on: table names, endpoints, code citations */
export function Src({ children }: { children: React.ReactNode }) {
  return <div className="pmk-src">{children}</div>;
}

export function Panel({ head, right, children, className }: { head?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={`pmc-panel ${className || ''}`}>
      {(head || right) && <div className="pmc-panel-head pmk-panel-head"><span>{head}</span>{right && <span className="pmc-grow" />}{right}</div>}
      {children}
    </div>
  );
}

export function ErrorBox({ title, msg }: { title: string; msg: string }) {
  return <div className="pmc-error"><div className="pmc-error-title">{title}</div><div className="pmc-error-msg">{msg}</div></div>;
}

export function Loading({ label }: { label: string }) {
  return <div className="pmc-empty pmk-loading">{label}</div>;
}

export function Num({ v, warn, bad }: { v: number | null | undefined; warn?: boolean; bad?: boolean }) {
  return <span className={`pmc-num${bad && v ? ' pmk-bad' : warn && v ? ' pmk-warn' : ''}`}>{fmt(v)}</span>;
}

/** a small tag next to a name: deep · draft · vN */
export function Tag({ kind, title, children }: { kind?: 'ok' | 'warn' | 'bad' | 'mute'; title?: string; children: React.ReactNode }) {
  return <span className={`pmk-tag ${kind || 'mute'}`} data-tip={title}>{children}</span>;
}

/** a "More actions" disclosure for the secondary and not-yet-wired actions of a chapter */
export function MoreActions({ items }: { items: Array<{ label: string; to?: string; href?: string; note?: string; disabled?: boolean }> }) {
  const list = items.filter(Boolean);
  if (!list.length) return null;
  return (
    <details className="pmk-more">
      <summary>More actions <span className="pmc-mute">{list.length}</span></summary>
      <div className="pmk-more-menu">
        {list.map((it) => (
          <React.Fragment key={it.label}>
            {it.to ? <a className="pmc-btn" href={it.to}>{it.label}</a>
              : it.href ? <a className="pmc-btn" href={it.href} target="_blank" rel="noreferrer">{it.label}</a>
                : <button type="button" className="pmc-btn" disabled title={it.note}>{it.label}</button>}
            <span className="pmc-small pmc-mute">{it.note}</span>
          </React.Fragment>
        ))}
      </div>
    </details>
  );
}
